/*
 * Copyright (C) 2011 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import i18nLolcalize from './i18nLolcalize.js'
import I18n from 'i18n-js'
import {
  extend as activateI18nliner,
  inferKey,
  normalizeDefault,
} from '@instructure/i18nliner-runtime'
// @canvas/i18n is ESM ("type":"module"); rspack fullySpecified needs .ts here.
import {localizeNumber} from '../i18next/localizeNumber.ts'
import logEagerLookupViolations from './logEagerLookupViolations.js'
import htmlEscape from '@instructure/html-escape'

activateI18nliner(I18n, {
  // this is what we use elsewhere in canvas, so make i18nliner use it too
  HtmlSafeString: htmlEscape.SafeString,

  // handle our absolute keys
  keyPattern: /^#?\w+(\.\w+)+$/,

  inferKey: (defaultValue, translateOptions) => `#${inferKey(defaultValue, translateOptions)}`,

  // when inferring the key at runtime (i.e. js/coffee or inline hbs `t`
  // call), signal to normalizeKey that it shouldn't be scoped.
  normalizeKey: (key, options) => {
    if (key[0] === '#') {
      return key.slice(1)
    } else if (options.scope) {
      return `${options.scope}.${key}`
    } else {
      return key
    }
  },

  normalizeDefault: window.ENV && window.ENV.lolcalize ? i18nLolcalize : normalizeDefault,
})

/*
 * Overridden interpolator that localizes any interpolated numbers.
 * Defaults to localizeNumber behavior (precision 9, but strips
 * insignificant digits). If you want a different format, do it
 * before you interpolate.
 */
const interpolate = I18n.interpolate.bind(I18n)

I18n.interpolate = function (message, origOptions) {
  const options = {...origOptions}
  const matches = message.match(I18n.placeholder) || []

  matches.forEach(placeholder => {
    const name = placeholder.replace(I18n.placeholder, '$1')
    if (typeof options[name] === 'number') {
      options[name] = this.localizeNumber(options[name])
    }
  })
  return interpolate(message, options)
}

I18n.locale = document.documentElement.getAttribute('lang')

I18n.lookup = logEagerLookupViolations(function (key, options = {}) {
  const locale = options.locale || I18n.currentLocale()
  const localeTranslations = I18n.translations[locale] || {}
  return localeTranslations[key] || options.defaultValue || null
})

const _localize = I18n.localize.bind(I18n)
I18n.localize = function (scope, value) {
  let result = _localize.call(this, scope, value)
  if (scope.match(/^(date|time)/)) result = result.replace(/\s{2,}/, ' ')
  return result
}

// Delegates to the i18next replacement (same delimiter/separator source
// as @canvas/i18n/numberFormat) so interpolate()/pluralize() below stay
// on the same number-format source of truth as the rest of the app.
I18n.n = I18n.localizeNumber = localizeNumber

// like the original, except it formats count
I18n.pluralize = function (count, scope, options) {
  let translation

  try {
    translation = this.lookup(scope, options)
  } catch (_error) {
    // no-op
  }

  if (!translation) {
    return this.missingTranslation(scope)
  }

  options = {precision: 0, ...options}
  options.count = this.localizeNumber(count, options)

  let message
  switch (Math.abs(count)) {
    case 0:
      message =
        translation.zero != null
          ? translation.zero
          : translation.none != null
            ? translation.none
            : translation.other != null
              ? translation.other
              : this.missingTranslation(scope, 'zero')
      break
    case 1:
      message = translation.one != null ? translation.one : this.missingTranslation(scope, 'one')
      break
    default:
      message =
        translation.other != null ? translation.other : this.missingTranslation(scope, 'other')
  }

  return this.interpolate(message, options)
}

export default I18n

/**
 * Adds or updates translations for a specific locale
 * @param {string} locale - The locale identifier (e.g., 'en', 'es')
 * @param {Object} translations - Key-value pairs of translation strings to add/update
 */
export const registerTranslations = (locale, translations) => {
  I18n.translations[locale] = I18n.translations[locale] || {}
  Object.assign(I18n.translations[locale], translations)
}

export const useTranslations = registerTranslations
